import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpResponse } from '@angular/common/http'; // Use HttpResponse for typed responses
import { Observable } from 'rxjs';

import { LocalDataSource } from '../local/local.data-source';
import { ServerSourceConf } from './server-source.conf';
import { getDeepFromObject } from '../../helpers';

import { map } from 'rxjs/operators';

export class ServerDataSource extends LocalDataSource {

  protected conf: ServerSourceConf;

  protected lastRequestCount: number = 0;

  constructor(protected http: HttpClient, conf: ServerSourceConf | {} = {}) {
    super();

    this.conf = new ServerSourceConf(conf);

    if (!this.conf.endPoint) {
      throw new Error(
        'At least endPoint must be specified as a configuration of the server data source.'
      );
    }
  }

  public count(): number {
    return this.lastRequestCount;
  }

  public getElements(): Promise<any> {
    return this.requestElements()
      .pipe(
        map((res: HttpResponse<any>) => {
          this.lastRequestCount = this.extractTotalFromResponse(res);
          this.data = this.extractDataFromResponse(res);
          return this.data;
        })
      )
      .toPromise();
  }

  /**
   * Extracts array of data from server response
   * @param res
   * @returns {any}
   */
  protected extractDataFromResponse(res: HttpResponse<any>): any[] {
    const rawData = res.body;
    const data = !!this.conf.dataKey ? getDeepFromObject(rawData, this.conf.dataKey, []) : rawData;
    if (Array.isArray(data)) {
      return data;
    }
    throw new Error(`
      Data must be an array.
      Please check that data extracted from the server response by the key
      '${this.conf.dataKey}' exists and is array.
    `);
  }

  /**
   * Extracts total rows count from the server response
   * Looks for the count in the heders first, then in the response body
   * @param res
   * @returns {any}
   */
  protected extractTotalFromResponse(res: HttpResponse<any>): number {
    if (res.headers.has(this.conf.totalKey)) {
      return +res.headers.get(this.conf.totalKey);
    } else {
      const rawData = res.body;
      return getDeepFromObject(rawData, this.conf.totalKey, 0);
    }
  }

  protected requestElements(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.conf.endPoint, {
      params: this.createRequestOptions(),
      observe: 'response'
    });
  }

  protected createRequestOptions(): HttpParams {
    let params = new HttpParams();
    params = this.addSortRequestOptions(params);
    params = this.addFilterRequestOptions(params);
    params = this.addPagerRequestOptions(params);
    return params;
  }

  protected addSortRequestOptions(requestOptions: HttpParams): HttpParams {
    let params = requestOptions;
    if (this.sortConf) {
      this.sortConf.forEach((fieldConf) => {
        params = params.set(this.conf.sortFieldKey, fieldConf.field);
        params = params.set(this.conf.sortDirKey, fieldConf.direction.toUpperCase());
      });
    }
    return params;
  }

  protected addFilterRequestOptions(requestOptions: HttpParams): HttpParams {
    let params = requestOptions;
    if (this.filterConf.filters) {
      this.filterConf.filters.forEach((fieldConf: any) => {
        if (fieldConf['search']) {
          params = params.set(
            this.conf.filterFieldKey.replace('#field#', fieldConf['field']),
            fieldConf['search']
          );
        }
      });
    }
    return params;
  }

  protected addPagerRequestOptions(requestOptions: HttpParams): HttpParams {
    let params = requestOptions;
    if (this.pagingConf && this.pagingConf['page'] && this.pagingConf['perPage']) {
      params = params.set(this.conf.pagerPageKey, this.pagingConf['page']);
      params = params.set(this.conf.pagerLimitKey, this.pagingConf['perPage']);
    }
    return params;
  }
}
